import { Button, Dropdown, Icon, Image, Menu, Message } from "semantic-ui-react";
import { Link, useLocation } from "react-router-dom";
import React, { useContext, useEffect, useState } from "react";

import { AuthContext } from "./AuthContextProviders";
import { useAuth0 } from "@auth0/auth0-react";
import whiteLogo from "../img/whitelogo.png";

const menuColor = "#023c5b";

interface MenuItem {
    key: string;
    as?: typeof Link;
    to?: string;
    href?: string;
    target?: string;
    content?: string;
    icon?: string;
    onClick?: () => void;
    type?: "divider";
}

const Navbar: React.FC = () => {
    const { user, logout } = useAuth0();
    const location = useLocation();
    const [isDesktop] = useState(window.matchMedia("(min-width: 768px)").matches);
    const { userRoles, internalUser } = useContext(AuthContext);
    const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
    const navbarRef = React.useRef<HTMLDivElement>(null);

    // Generate menu items based on user state and screen size
    useEffect(() => {
        const items: MenuItem[] = [];

        if (user) {
            // Home
            items.push({
                key: "home",
                as: Link,
                to: "/trust",
                content: "Home",
                icon: "home"
            });
            items.push({
                key: "support",
                as: Link,
                to: "/support",
                content: "Support",
                icon: "help circle"
            });
        }

        if (user) {
            // Update Contact Details (conditional)
            if (
                !!userRoles &&
                userRoles?.some(
                    (type) =>
                        type.ContactType === "Grantor" ||
                        type.ContactType === "Beneficiary" ||
                        type.ContactType === "Conservator" ||
                        type.ContactType === "Guardian" ||
                        type.ContactType === "Power of Attorney"
                )
            ) {
                items.push({
                    key: "update-contacts",
                    as: Link,
                    to: "/update-contacts",
                    content: "Update Contact Information",
                    icon: "edit"
                });
            }

            // Communication Preferences
            items.push({
                key: "communication",
                as: Link,
                to: "/communication",
                content: "Communication Preferences",
                icon: "envelope"
            });

            // External links (mobile only)
            if (!isDesktop) {
                items.push({ key: "divider1", type: "divider" });
                items.push({
                    key: "grantor-resources",
                    href: "https://lifeinsurancetrustco.com/grantors-beneficiaries/?utm_source=portal&utm_content=GBButton",
                    content: "Grantor Beneficiary Resources",
                    target: "_blank"
                });
                items.push({
                    key: "blog",
                    href: "https://lifeinsurancetrustco.com/blog?utm_source=portal&utm_content=BlogButton",
                    target: "_blank",
                    content: "Blog"
                });
                items.push({ key: "divider2", type: "divider" });
            }

            items.push({
                key: "logout",
                icon: "lock",
                onClick: () => logout(),
                content: "Log Out"
            });
        }

        setMenuItems(items);
    }, [user, userRoles, isDesktop, logout]);

    const renderMenuItems = (items: MenuItem[], displayAsButton: boolean = isDesktop) => {
        return items.map((item) => {
            if (item.type === "divider") {
                return <Dropdown.Divider key={item.key} />;
            }
            const { key, type, icon, content, ...menuItemProps } = item;
            if (displayAsButton) {
                return (
                    <Menu.Item key={key} style={{ padding: "10px" }}>
                        <Button
                            style={{ color: menuColor, backgroundColor: "white" }}
                            content={content?.toUpperCase()}
                            {...menuItemProps}
                        />
                    </Menu.Item>
                );
            }
            return <Menu.Item key={key} icon={icon} color="blue" content={content} {...menuItemProps} />;
        });
    };

    const renderInternalUserMessage = () => {
        if (!internalUser || !internalUser.IsInternal) return null;

        return (
            <Message attached negative style={{ marginTop: "-14px" }}>
                <Message.Header>
                    <Icon name="user secret" />
                    Impersonating {internalUser.FullName} ({internalUser.ContactID}), {internalUser.EmailAddress}
                </Message.Header>
            </Message>
        );
    };

    if (isDesktop) {
        return (
            <>
                <div
                    ref={navbarRef}
                    style={{
                        backgroundColor: menuColor,
                        display: "flex",
                        justifyContent: "center",
                        position: "sticky",
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.5)"
                    }}
                >
                    <Menu
                        stackable
                        tabular
                        style={{
                            paddingLeft: "20px",
                            paddingRight: "20px",
                            marginBottom: "0px",
                            borderBottom: "0",
                            maxWidth: "1500px",
                            width: "100%"
                        }}
                    >
                        <Menu.Item>
                            {location.pathname === "/register" ? (
                                <a href="https://lifeinsurancetrustco.com/">
                                    <Image size="small" src={whiteLogo} />
                                </a>
                            ) : (
                                <Link to="/trust">
                                    <Image size="small" src={whiteLogo} />
                                </Link>
                            )}
                        </Menu.Item>
                        <Menu.Menu position="right">{renderMenuItems(menuItems)}</Menu.Menu>
                    </Menu>
                </div>
                <div style={{ height: "10px" }} />
                {renderInternalUserMessage()}
            </>
        );
    }

    return (
        <>
            <Menu tabular fitted pointing style={{ backgroundColor: menuColor }}>
                <Menu.Item>
                    <Link to="/trust">
                        <Image size="small" src={whiteLogo} />
                    </Link>
                </Menu.Item>
                <Menu.Menu position="right">
                    {user ? (
                        <Dropdown direction="left" item icon="bars" style={{ color: "white" }}>
                            <Dropdown.Menu>{renderMenuItems(menuItems)}</Dropdown.Menu>
                        </Dropdown>
                    ) : (
                        renderMenuItems(menuItems, true)
                    )}
                </Menu.Menu>
            </Menu>
            {renderInternalUserMessage()}
        </>
    );
};

export default Navbar;
